<script setup lang="ts">
// Task 11 of the 2026-09-16 gold/GGE strike-anchor arc (PR D, shadow-mode
// observability layer) — the visual counterpart to Task 10's
// GateSessionTracker (which counts clean sessions and fires WhatsApp on
// drift, but has no pane surface of its own).
//
// 2026-09-22 (paolo): restyled to match the K_t pane's tile family
// (.tile / .tile-label / .tile-row / .kt-legend), moved on to the KT tab
// beneath the K_t magnitude tiles + the step-aside anchor. Row labels
// gained an "(i)" legend explaining what the shadow is, what a drift
// number represents, why some rows can read amber and still be fine
// (JH steady-state noise from canonical/shadow magnitude mismatch —
// documented in BgaJhShadowReconciler's own class remarks). Endpoint
// now recognises that JH steady state and renders it grey rather than a
// permanent "FAILED".
import { ref, computed } from 'vue'
import { useStrikeAnchorHealth } from '../composables/useStrikeAnchorHealth'
import type { StrikeAnchorFundHealth } from '../api/client'

const { funds, available } = useStrikeAnchorHealth()
const legendOpen = ref(false)

const fundLabels: Record<string, string> = { bga: 'BGA', gold: 'Gold', gge: 'GGE', jh: 'JH', japan: 'L&G Japan', charteris: 'Charteris', amati: 'Amati', miners: 'Miners', japanesg: 'Japan ESG', tech: 'Tech', electrum: 'Electrum',
  jhtech: 'JHTech', asiatech: 'AsiaTech', baringsoil: 'BaringsOil', spworld: 'SPWorld', smallcap: 'SmallCap' }

// Per-fund strike time, purely informational for the (i) legend.
const strikeTimes: Record<string, string> = {
  bga: '10:00 London',
  gold: '12:00 London',
  gge: '12:00 London',
  jh: '12:00 London',
  // 2026-10-06: gated — only present when the server sent the row (entitled caller).
  japan: '12:00 London',
  // 2026-10-11: gated — only present when the server sent the row (entitled caller or superuser preview).
  charteris: '12:00 London',
  amati: '12:00 London',
  miners: '12:00 London',
  japanesg: '12:00 London',
  tech: '12:00 London',
  electrum: '12:00 London',
  jhtech: '12:00 London',
  asiatech: '12:00 London',
  baringsoil: '12:00 London',
  spworld: '12:00 London',
  smallcap: '12:00 London',
}

function statusChipClass(status: string): string {
  return `sa-chip sa-chip--${status}`
}

// Compact row status label: what the operator should read at a glance.
// - green   → OK
// - amber + verdictPassed === false → DRIFT (aggregate is above threshold —
//   real signal, worth a look, not a system fault)
// - amber + verdict null (past reconcile window) → WATCH
// - red     → ALERT
// - grey    → the state's detail line explains why (pre-strike,
//   non-dealing day, canonical not yet published, etc)
// Ruling R9 (2026-09-30 BGA threshold-anchor arc, final review; generalised
// to JH by the 2026-10-01 JH NAV-accuracy arc Task 7): BGA's and JH's
// status is anchor-freshness-only — the endpoint never populates their
// VerdictPassed/AggregateDriftBp, so this never reads true for either, but
// the explicit `!usesAccuracyGate(row)` guard keeps the row's "no DRIFT
// chip" invariant true from the template's side too, not just as a
// trust-the-backend assumption.
function usesAccuracyGate(row: StrikeAnchorFundHealth): boolean {
  return row.fundId === 'bga' || row.fundId === 'jh'
}

function rowStatusLabel(row: StrikeAnchorFundHealth): string {
  if (row.status === 'green') return 'OK'
  if (row.status === 'red') return 'ALERT'
  if (row.status === 'amber') return !usesAccuracyGate(row) && row.verdictPassed === false ? 'DRIFT' : 'WATCH'
  return 'IDLE'
}

// Human-friendly detail. Keep the endpoint's own copy where it's meaningful
// (accuracy-gate text, pre-strike, non-dealing day…), collapse the noisy
// "drift X.Xbp > Ybp" one into a compact "vs Ybp" so the row stays short.
function rowDetail(row: StrikeAnchorFundHealth): string {
  if (row.status === 'amber' && row.verdictPassed === false && row.thresholdBp !== null && row.thresholdBp !== undefined) {
    return `vs ${row.thresholdBp}bp gate`
  }
  return row.detail
}

function driftDisplay(row: StrikeAnchorFundHealth): string {
  const bp = row.aggregateDriftBp
  if (bp === null || bp === undefined) return '—'
  return `${bp.toFixed(2)} bp`
}

// Task 7 (2026-09-30 BGA threshold-anchor arc §4; generalised to JH by the
// 2026-10-01 JH NAV-accuracy arc Task 7) — accuracy gate, replacing the
// parity-drift figure for that row. accuracyScoredDays is the presence
// signal: it's only ever set (by StrikeAnchorHealthEndpoint) on a row in
// its AccuracyFunds set (BGA, JH), and only once the endpoint has an
// accuracy repo wired.
function hasAccuracyGate(row: StrikeAnchorFundHealth): boolean {
  return row.accuracyScoredDays !== undefined && row.accuracyScoredDays !== null
}

function accuracyDisplay(row: StrikeAnchorFundHealth): string {
  const scored = row.accuracyScoredDays ?? 0
  const t = row.thresholdRmseBp
  const lp = row.lastPrintRmseBp
  const tStr = t === null || t === undefined ? '—' : t.toFixed(1)
  const lpStr = lp === null || lp === undefined ? '—' : lp.toFixed(1)
  // Minor #7 (final review): "n/20" reads as warm-up progress toward the
  // gate's 20-day minimum — once scored exceeds 20 that framing is wrong
  // ("34/20" looks like a bug, not "well past the minimum").
  const scoredLabel = scored <= 20 ? `${scored}/20 days` : `${scored} days`
  let text = `${scoredLabel} · ${tStr}bp vs ${lpStr}bp`
  if (row.accuracyExcludedDays !== null && row.accuracyExcludedDays !== undefined && row.accuracyExcludedDays > 0) {
    text += ` · ${row.accuracyExcludedDays} excluded`
  }
  return text
}

// Bound 0/unset means the gate can never be met (AnchorAccuracyMath.Gate
// requires boundBp > 0) — show "—" rather than a misleading "0bp".
function accuracyBoundDisplay(row: StrikeAnchorFundHealth): string {
  const bound = row.accuracyBoundBp
  if (bound === null || bound === undefined || bound <= 0) return '—'
  return `${bound}bp`
}

function accuracyTooltip(row: StrikeAnchorFundHealth): string {
  // Fix round (2026-10-01 JH NAV-accuracy arc Task 7 review): the candidate
  // rule name is fund-specific (BGA's permanent "threshold", JH's
  // configured "ah_close"/"threshold") — read it from the row rather than
  // hardcoding the literal word "threshold", which was only ever correct
  // for BGA.
  const candidate = row.accuracyCandidateRule ?? 'threshold'
  return `${candidate} RMSE vs last-print RMSE; bound ${accuracyBoundDisplay(row)}`
}

// Strike-anchor go-live arc PR-1 (2026-10-14): the accuracy go-live readout
// (bga/gold/gge). goLiveScoredSessions is the presence signal — only set by
// StrikeAnchorHealthEndpoint.WithGoLiveAsync. Rendered on its own line under
// the row, alongside (never instead of) the parity streak.
function hasGoLive(row: StrikeAnchorFundHealth): boolean {
  return row.goLiveScoredSessions !== undefined && row.goLiveScoredSessions !== null
}

function goLiveDisplay(row: StrikeAnchorFundHealth): string {
  const n = row.goLiveScoredSessions ?? 0
  const window = row.goLiveWindow ?? 10
  if (n === 0) return 'go-live vs NAV: no scored sessions yet'
  const s = row.goLiveShadowMaeBp
  const i = row.goLiveIncumbentMaeBp
  const sStr = s === null || s === undefined ? '—' : s.toFixed(1)
  const iStr = i === null || i === undefined ? '—' : i.toFixed(1)
  const shadow = row.goLiveShadowRule ?? 'shadow'
  const incumbent = row.goLiveIncumbentRule ?? 'incumbent'
  return `go-live vs NAV ${Math.min(n, window)}/${window} · ${shadow} ${sStr}bp vs ${incumbent} ${iStr}bp MAE`
}

// BGA/JH promotion (2026-10-24): books go-live readout, shadow vs Python incumbent.
// booksScoredSessions is the presence signal (only bga/jh rows carry it).
function hasBooks(row: StrikeAnchorFundHealth): boolean {
  return row.booksScoredSessions !== undefined && row.booksScoredSessions !== null
}

function booksDisplay(row: StrikeAnchorFundHealth): string {
  const n = row.booksScoredSessions ?? 0
  const window = row.booksWindow ?? 10
  const s = row.booksShadowMaeBp
  const p = row.booksPythonMaeBp
  const sStr = s === null || s === undefined ? '—' : s.toFixed(1)
  const pStr = p === null || p === undefined ? '—' : p.toFixed(1)
  const shadow = row.goLiveShadowRule ?? 'threshold'
  return `books vs Python ${Math.min(n, window)}/${window} · ${shadow} ${sStr}bp vs python ${pStr}bp MAE`
}

function formatUkTime(isoUtc: string | null): string {
  if (!isoUtc) return '—'
  const d = new Date(isoUtc)
  if (Number.isNaN(d.getTime())) return '—'
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d)
  const map: Record<string, string> = {}
  for (const p of parts) if (p.type !== 'literal') map[p.type] = p.value
  if (!map.hour || !map.minute) return '—'
  return `${map.hour}:${map.minute}`
}

// Kept for the existing test that checks the verdict text on a failed row.
// (New template uses rowStatusLabel; this survives as a data hook.)
function verdictLabel(row: StrikeAnchorFundHealth): string {
  if (row.verdictPassed === true) return 'passed'
  if (row.verdictPassed === false) return 'FAILED'
  return 'pending'
}

const rows = computed(() => funds.value)
</script>

<template>
  <div class="tile sa-health-tile" data-testid="strike-anchor-health-tile">
    <div class="tile-label sa-health-title">
      <span>Strike anchor <span class="sa-mode">(shadow)</span></span>
      <button
        type="button"
        class="kt-info-btn kt-info-btn--tile"
        :aria-expanded="legendOpen"
        aria-controls="strike-anchor-health-legend"
        :aria-label="legendOpen ? 'Hide strike-anchor explanation' : 'Show strike-anchor explanation'"
        data-testid="strike-anchor-info-toggle"
        @click="legendOpen = !legendOpen"
      >i</button>
    </div>

    <div v-if="!available" class="sa-health-empty">—</div>

    <div v-else class="sa-health-rows">
      <div class="sa-health-head" aria-hidden="true">
        <span>Fund</span><span>Status</span><span>Strike</span><span>Measure</span><span class="sa-head-run">Run</span>
      </div>
      <div
        v-for="row in rows"
        :key="row.fundId"
        class="sa-health-row"
        :data-testid="`strike-anchor-health-row-${row.fundId}`"
      >
        <span class="sa-fund">{{ fundLabels[row.fundId] ?? row.fundId.toUpperCase() }}</span>
        <span class="sa-status"><span :class="statusChipClass(row.status)">{{ rowStatusLabel(row) }}</span></span>
        <span class="sa-strike" :title="`Strike ${formatUkTime(row.anchorFreshUtc ?? row.strikeTimeExpectedUtc)} UK`">
          {{ formatUkTime(row.anchorFreshUtc ?? row.strikeTimeExpectedUtc) }} UK
        </span>
        <!-- Measure: the accuracy gate (BGA/JH/Japan) or the parity drift + verdict (everyone else). -->
        <span class="sa-measure">
          <span
            v-if="hasAccuracyGate(row)"
            class="sa-drift sa-accuracy"
            :title="accuracyTooltip(row)"
            data-testid="strike-anchor-accuracy"
          >
            {{ accuracyDisplay(row) }}
            <span v-if="row.accuracyGateMet" class="sa-chip sa-chip--green sa-gate-badge">GATE MET</span>
          </span>
          <template v-else>
            <span class="sa-drift" :title="row.perHoldingWorstTicker ? `Worst holding: ${row.perHoldingWorstTicker} (${row.perHoldingMaxDriftBp?.toFixed(1)}bp)` : ''">
              {{ driftDisplay(row) }}
            </span>
            <span
              v-if="!usesAccuracyGate(row)"
              class="sa-verdict"
              :class="{ 'sa-verdict-fail': row.verdictPassed === false && row.status !== 'grey' }"
            >
              {{ verdictLabel(row) }}
            </span>
          </template>
          <span v-if="row.totalSource === 'shadow'" class="sa-chip sa-chip--green sa-live-total" data-testid="strike-anchor-live-total" title="This fund's HoldingsPanel TOTAL row reads the C# strike anchor">LIVE TOTAL</span>
        </span>
        <span class="sa-run">
          <span v-if="!usesAccuracyGate(row)" class="sa-count" :title="'Consecutive clean dealing sessions'">{{ row.cleanSessionCount }}d</span>
        </span>
        <!-- Second line: the endpoint's detail, plus (L&G Japan) the noon shadow drift that sits beside its accuracy
             gate. 2026-10-06: a row can carry BOTH; BGA/JH never receive aggregateDriftBp, so their rows are unchanged. -->
        <span class="sa-sub sa-detail-line">
          <template v-if="hasAccuracyGate(row) && row.aggregateDriftBp !== null && row.aggregateDriftBp !== undefined">
            <span class="sa-drift sa-drift-inline" :title="row.perHoldingWorstTicker ? `Worst holding: ${row.perHoldingWorstTicker} (${row.perHoldingMaxDriftBp?.toFixed(1)}bp)` : ''">noon shadow {{ driftDisplay(row) }}</span>
            <span v-if="!usesAccuracyGate(row)" class="sa-verdict" :class="{ 'sa-verdict-fail': row.verdictPassed === false && row.status !== 'grey' }">{{ verdictLabel(row) }}</span>
            <span class="sa-sep">·</span>
          </template>
          <span class="sa-detail">{{ rowDetail(row) }}</span>
        </span>
        <!-- Third line: the accuracy go-live readout (BGA, Gold, GGE). -->
        <span
          v-if="hasGoLive(row)"
          class="sa-sub sa-golive"
          title="Mean |error| vs the official NAV over the newest scored sessions; met = full window and shadow MAE at or below incumbent MAE"
          data-testid="strike-anchor-golive"
        >
          {{ goLiveDisplay(row) }}
          <span v-if="row.goLiveGateMet" class="sa-chip sa-chip--green sa-gate-badge">GO-LIVE MET</span>
        </span>
        <!-- Fourth line: books go-live readout vs the Python incumbent (BGA, JH). -->
        <span
          v-if="hasBooks(row)"
          class="sa-sub sa-golive"
          title="Mean |error| of each anchor vs the fund's booked strike over the newest scored sessions; met = full window and shadow MAE at or below Python MAE"
          data-testid="strike-anchor-books"
        >
          {{ booksDisplay(row) }}
          <span v-if="row.booksGateMet" class="sa-chip sa-chip--green sa-gate-badge">BOOKS GATE MET</span>
        </span>
      </div>
    </div>

    <section
      v-if="legendOpen"
      id="strike-anchor-health-legend"
      class="kt-legend sa-legend"
      role="region"
      aria-label="Strike anchor (shadow) explanation"
      data-testid="strike-anchor-legend"
    >
      <p class="kt-legend-lead">
        The <b>strike anchor</b> is the synthetic-NAV reading each fund freezes at its
        <b>strike time</b> — the exact moment its daily dealing NAV is decided
        (<code>{{ strikeTimes.bga }}</code> for BGA, <code>12:00 London</code> for
        Gold, GGE, JH). The <b>shadow</b> is a parallel computation of the same
        anchor by the new C# path (<code>SynthNavStrikeAnchorBase</code>). Every
        session the shadow is compared against the incumbent canonical writer;
        the drift is what this tile surfaces. Read-only observability — nothing
        on the live dealing path depends on it yet.
        <b>LIVE TOTAL</b>: the pane's TOTAL row is on the shadow anchor
        (<code>config:override:strike_anchor:{fund}:total_source</code>).
      </p>

      <dl class="kt-legend-list">
        <dt>Reading a row</dt>
        <dd>
          <b>OK</b> — verdict passed (Gold/GGE), or anchor fresh (BGA/JH),
          shadow within its gate.
          <b>DRIFT</b> — Gold/GGE only: verdict FAILED, aggregate drift above
          the fund's 5bp gate; the number to the right is that drift. BGA and
          JH never show DRIFT — see "accuracy gate" below.
          <b>WATCH</b> — verdict expected past the 15-minute reconcile grace and
          hasn't arrived (reconciler may be down).
          <b>ALERT</b> — anchor still missing 30+ minutes past strike (freeze
          should have fired).
          <b>IDLE</b> — pre-strike, non-dealing day, or canonical not yet
          published.
        </dd>

        <dt>BGA and JH use an accuracy gate, not DRIFT</dt>
        <dd>
          BGA and JH never show DRIFT: their row's status comes from
          <b>anchor freshness alone</b> — a fresh anchor is OK regardless of
          what the parity-drift comparison below would have said, because
          that comparison is never run for these two funds. In its place,
          the number on the right is the <b>accuracy-grade cutover gate</b>:
          scored days (out of the 20-day minimum), the fund's candidate
          rule's RMSE against the last-print RMSE (both in bp — official NAV
          vs each rule's predicted return), and the configured bound. A
          <b>GATE MET</b> badge appears once there are at least 20 scored
          days and the candidate's RMSE is at or below both last-print's
          RMSE and the bound. BGA's candidate is the permanent
          <code>threshold</code> rule; JH's is its configured candidate
          (named in the row's tooltip).
        </dd>

        <dt>What the drift number tells you (Gold, GGE)</dt>
        <dd>
          Aggregate drift in bp between the shadow anchor and the fund's
          existing canonical writer. Numbers under the gate mean the two paths
          agree well enough for a future cutover; persistent numbers above the
          gate mean either the shadow needs re-calibration or the canonical
          writer is drifting relative to it. The tooltip on the drift number
          also lists the worst-holding ticker so you can jump straight to
          which holding is misaligned.
        </dd>

        <dt>Go-live vs the official NAV (BGA, Gold, GGE)</dt>
        <dd>
          The parity check above asks whether the shadow agrees with the incumbent; the
          <b>go-live line</b> under the row asks which one predicts the <b>official NAV</b> better.
          Each scored session compares both anchors' predicted return with the published NAV
          return; the line shows the mean absolute error of each over the newest 10 scored
          sessions. Gold and GGE compare the C# <code>shadow</code> anchor with the hybrid's
          <code>incumbent</code> strike prices, on the same names, weights and FX. BGA compares its
          two C# rules (<code>threshold</code> vs <code>last_print</code>) — the Python incumbent has no
          accuracy history. <b>GO-LIVE MET</b> needs 10 scored sessions and the shadow's error at or
          below the incumbent's. Gold and GGE parity breaches no longer send WhatsApp: the gold shadow
          fails parity for being right (Toronto names at the prior close, as the fund prices them), and the
          GGE anchor method is changing by design. BGA and JH rows add a <b>books vs Python</b> line:
          the same mean absolute error, but scored against the fund's booked (contract-note) strike,
          comparing the C# shadow with the Python incumbent. Phase 2 of the runbook waits for
          <b>BOOKS GATE MET</b>.
        </dd>

        <dt>Charteris</dt>
        <dd>
          The anchor is the <b>noon strike</b> frozen at 12:00 London from what is knowable then
          (North American names at the prior close, TSX line for TSX-primary names; FX from the
          prior day; a London/ASX name without a same-day close is left out). It is compared with
          the <b>booked strike</b> in the daily K_t series once the official NAV lands (~15:30 UK),
          so the row stays IDLE until then. The drift is mostly the FX roll to the end-of-day rate;
          the 60bp gate flags a missing price fetch, not an FX day. Shadow only — never a decision input.
        </dd>

        <dt>Refresh</dt>
        <dd>
          The endpoint reads Redis, so this tile is only as fresh as the last
          reconciler tick (5 min, or 15 min after strike for the first run).
          The composable re-polls every <b>60 s</b>.
        </dd>
      </dl>
    </section>
  </div>
</template>

<style scoped>
.sa-health-tile {
  gap: 0.35rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
}

.sa-health-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  text-transform: none;
  letter-spacing: normal;
  color: #ccc;
}
.sa-mode { color: #888; font-weight: 500; font-size: 0.7rem; }

.sa-health-empty { color: #888; }

/* One fixed grid for every fund: fund | status | strike | measure | run. A row's second and third lines
   (detail, go-live) span the measure+run columns, so every number sits under the same heading. */
.sa-health-rows { display: flex; flex-direction: column; }

.sa-health-head,
.sa-health-row {
  display: grid;
  grid-template-columns: 5.6rem 4.4rem 4.6rem minmax(0, 1fr) 2.6rem;
  column-gap: 0.6rem;
  align-items: center;
}

.sa-health-head {
  padding: 0 0 0.3rem 0;
  color: #666;
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 1px solid #2a2a2a;
}
.sa-head-run { text-align: right; }

.sa-health-row {
  row-gap: 0.15rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid #222;
}
.sa-health-row:last-child { border-bottom: none; }

.sa-fund { color: #ddd; font-weight: 600; white-space: nowrap; }
.sa-status { display: flex; }

.sa-chip {
  display: inline-block;
  width: max-content;
  font-weight: 700;
  font-size: 0.68rem;
  line-height: 1.3;
  letter-spacing: 0.05em;
  text-align: center;
  padding: 2px 6px;
  border-radius: 3px;
  background: #ffffff10;
  color: #aaa;
  white-space: nowrap;
}
.sa-status .sa-chip { min-width: 3.9rem; }
.sa-chip--green { color: #4caf50; background: #4caf5019; }
.sa-chip--amber { color: #fbc02d; background: #fbc02d19; }
.sa-chip--red   { color: #f44336; background: #f4433622; }

.sa-strike { color: #999; font-size: 0.75rem; white-space: nowrap; }

.sa-measure {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.55rem;
  min-width: 0;
}
.sa-drift  { color: #ddd; font-size: 0.75rem; white-space: nowrap; }
.sa-accuracy { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 0.25rem 0.45rem; white-space: normal; }
.sa-gate-badge { display: inline-block; }
.sa-verdict { color: #888; font-size: 0.72rem; }
.sa-verdict-fail { color: #f44336; font-weight: 700; }

.sa-run { text-align: right; }
.sa-count { color: #777; font-size: 0.72rem; white-space: nowrap; }

.sa-sub {
  grid-column: 4 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.45rem;
  min-width: 0;
  color: #888;
  font-size: 0.72rem;
}
.sa-detail-line { color: #8a8a8a; }
.sa-drift-inline { color: #bbb; font-size: 0.72rem; }
.sa-sep { color: #555; }
.sa-detail { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.sa-golive { color: #8f8f8f; }

/* Narrow viewports: the measure drops to its own full-width line under fund/status/strike/run;
   nothing is clipped and every chip keeps its intrinsic width. */
@media (max-width: 640px) {
  .sa-health-head { display: none; }
  .sa-health-row {
    grid-template-columns: 5.2rem 4.4rem minmax(0, 1fr) 2.6rem;
    row-gap: 0.25rem;
  }
  .sa-fund   { grid-row: 1; grid-column: 1; }
  .sa-status { grid-row: 1; grid-column: 2; }
  .sa-strike { grid-row: 1; grid-column: 3; }
  .sa-run    { grid-row: 1; grid-column: 4; }
  .sa-measure { grid-row: 2; grid-column: 1 / -1; }
  .sa-sub { grid-column: 1 / -1; }
  .sa-detail { white-space: normal; }
}

/* The legend: .kt-legend lives in KtTrackingPanel's scoped styles, so this tile carries its own copy. */
.sa-legend {
  margin-top: 0.5rem;
  background: #141414;
  border: 1px solid #2a2a2a;
  border-radius: 6px;
  padding: 0.75rem 0.9rem;
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
  font-size: 0.78rem;
  line-height: 1.45;
  color: #b3b3b3;
}
.sa-legend code { font-family: 'JetBrains Mono', monospace; font-size: 0.72rem; color: #ccc; }
.sa-legend .kt-legend-lead { margin: 0 0 0.65rem 0; }
.sa-legend .kt-legend-list { margin: 0.4rem 0 0.2rem 0; }
.sa-legend .kt-legend-list dt { margin-top: 0.6rem; color: #ddd; font-weight: 600; }
.sa-legend .kt-legend-list dt:first-of-type { margin-top: 0; }
.sa-legend .kt-legend-list dd { margin: 0.15rem 0 0 0; }
</style>
